import { cn, cva, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import type { ReactNode } from 'react';

export type FilterChipTone = 'muted' | 'warning' | 'violet' | 'amber' | 'emerald' | 'slate' | 'red' | 'rose';

// Resting chips are neutral; color appears when a chip is active — either because
// it's selected, or because nothing is selected at all (then every chip shows its
// tone, since "no filter = everything shown"). `tone` picks that active color.
const filterChipBase =
  'group inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary';

// Active = a deeper fill in the chip's own tone (strong same-tone border + bolder
// tint). `hover:*` re-pins it so hovering an active chip doesn't lighten it back.
const activeToneClass = cva('', {
  variants: {
    tone: {
      muted: 'border-foreground bg-muted text-foreground hover:bg-muted',
      warning: 'border-amber-500 bg-amber-100 text-amber-900 hover:bg-amber-100',
      violet: 'border-violet-500 bg-violet-100 text-violet-800 hover:bg-violet-100',
      amber: 'border-amber-500 bg-amber-100 text-amber-900 hover:bg-amber-100',
      emerald: 'border-emerald-500 bg-emerald-100 text-emerald-900 hover:bg-emerald-100',
      slate: 'border-slate-500 bg-slate-100 text-slate-900 hover:bg-slate-100',
      red: 'border-red-500 bg-red-100 text-red-900 hover:bg-red-100',
      rose: 'border-rose-500 bg-rose-100 text-rose-900 hover:bg-rose-100'
    }
  }
});

export interface NoddiFilterChipProps {
  icon: NoddiIconName;
  label: string;
  /** This chip is in the selection — drives the clear-X and aria-pressed. */
  isActive: boolean;
  /**
   * No filter is applied (empty selection). The chip then shows its active tone
   * like every other one — "nothing filtered = everything shown" — but without
   * the clear-X, since clicking isolates to this chip rather than clearing it.
   */
  allActive?: boolean;
  onClick: () => void;
  tone?: FilterChipTone;
  /** Small inline value after the label — a count, "None", a "Failed to load" node, etc. */
  count?: ReactNode;
  /** Override the hover tooltip; defaults to a filter/clear hint. */
  title?: string;
  className?: string;
}

/**
 * Plain pill that toggles one value of a multiselect filter on the list or grid
 * it summarizes — a leading icon, a label, and an optional inline count. Colored
 * in its `tone` when active or when nothing is selected (all-on default); only a
 * genuinely-selected chip shows the clear-X. Shared chrome so absence lists,
 * transfer-order issues, and the worker calendar can't drift apart. Selection
 * state is owned by the caller; `toggleFilterValue` adds/removes a value and
 * `matchesFilter` implements the shared "empty selection = show all" read
 * semantics (pass `allActive` as `selection.length === 0`).
 */
export const NoddiFilterChip = ({
  icon,
  label,
  isActive,
  allActive = false,
  onClick,
  tone = 'muted',
  count,
  title,
  className
}: NoddiFilterChipProps) => {
  const colored = isActive || allActive;
  return (
    <button
      type='button'
      onClick={onClick}
      aria-pressed={isActive}
      title={title ?? (isActive ? 'Click to clear filter' : `Filter by ${label.toLowerCase()}`)}
      // Icon, label and count inherit the chip's color via currentColor, so the
      // whole pill shifts together between neutral and its active tone.
      className={cn(filterChipBase, colored && activeToneClass({ tone }), className)}
    >
      <NoddiIcon name={icon} size='small' className='shrink-0' />
      <span>{label}</span>
      {count != null && <span className='font-semibold tabular-nums'>{count}</span>}
      {isActive && <NoddiIcon name='X' size={14} className='shrink-0' />}
    </button>
  );
};
